import React, { useState, useRef } from 'react';
import { 
  Upload, FileText, CheckCircle2, AlertCircle, X, 
  ChevronRight, Info, Table as TableIcon, Download,
  Settings, Check, AlertTriangle, ArrowRight, MousePointer2
} from 'lucide-react';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter,
  DialogHeader, DialogTitle,
} from "../ui/dialog";
import { Button } from "../ui/button";
import { Badge } from "../ui/badge";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "../ui/table";
import API from '../api/api01';
import { toast } from 'sonner';
import { cn } from '../ui/utils';

interface CSVImportGeneralLeadModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onImportSuccess: () => void;
}

type ImportStep = 'upload' | 'mapping' | 'review';

export default function CSVImportGeneralLeadModal({ open, onOpenChange, onImportSuccess }: CSVImportGeneralLeadModalProps) {
  const [step, setStep] = useState<ImportStep>('upload');
  const [file, setFile] = useState<File | null>(null);
  const [csvData, setCsvData] = useState<any[]>([]);
  const [headers, setHeaders] = useState<string[]>([]);
  const [isParsing, setIsParsing] = useState(false);
  const [isImporting, setIsImporting] = useState(false);
  const [skipDuplicates, setSkipDuplicates] = useState(true);
  const [editingCell, setEditingCell] = useState<{row: number, field: string} | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Field mapping state for General Leads
  const EXPECTED_FIELDS = [
    { key: 'name', label: 'Student Name', required: true },
    { key: 'email', label: 'Email', required: true },
    { key: 'phone', label: 'Phone Number', required: false },
    { key: 'course', label: 'Applied Course', required: false },
    { key: 'source', label: 'Lead Source', required: false },
    { key: 'priority', label: 'Priority', required: false },
    { key: 'notes', label: 'Notes', required: false },
  ];

  const [mapping, setMapping] = useState<Record<string, string>>({});

  const resetState = () => {
    setStep('upload');
    setFile(null);
    setCsvData([]);
    setHeaders([]);
    setMapping({});
    setIsParsing(false);
    setIsImporting(false);
    setSkipDuplicates(true);
    setEditingCell(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  React.useEffect(() => {
    if (!open) {
      const timer = setTimeout(resetState, 300);
      return () => clearTimeout(timer);
    }
  }, [open]);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) processFile(file);
  };

  const processFile = (file: File) => {
    if (file.type !== 'text/csv' && !file.name.endsWith('.csv')) {
      toast.error("Please upload a valid CSV file");
      return;
    }
    setFile(file);
    parseCSV(file);
  };

  const parseCSV = (file: File) => {
    setIsParsing(true);
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const text = e.target?.result as string;
        const lines = text.split(/\r?\n/).filter(line => line.trim() !== '');
        if (lines.length < 2) {
          toast.error("CSV file is empty or missing data");
          setIsParsing(false);
          setFile(null);
          return;
        }

        const csvHeaders = lines[0].split(',').map(h => h.trim().replace(/^"|"$/g, ''));
        setHeaders(csvHeaders);

        const data = lines.slice(1).map(line => {
          const values: string[] = [];
          let current = '';
          let inQuotes = false;
          for (let i = 0; i < line.length; i++) {
            const char = line[i];
            if (char === '"') inQuotes = !inQuotes;
            else if (char === ',' && !inQuotes) {
              values.push(current.trim());
              current = '';
            } else {
              current += char;
            }
          }
          values.push(current.trim());
          
          const obj: any = {};
          csvHeaders.forEach((header, index) => {
            obj[header] = values[index]?.replace(/^"|"$/g, '') || '';
          });
          return obj;
        });

        setCsvData(data);
        
        const newMapping: Record<string, string> = {};
        EXPECTED_FIELDS.forEach(field => {
          const match = csvHeaders.find(h => 
            h.toLowerCase() === field.key.toLowerCase() || 
            h.toLowerCase() === field.label.toLowerCase() ||
            (field.key === 'name' && h.toLowerCase().includes('name')) ||
            (field.key === 'phone' && h.toLowerCase().includes('phone')) ||
            (field.key === 'email' && h.toLowerCase().includes('email'))
          );
          if (match) newMapping[field.key] = match;
        });
        setMapping(newMapping);
        setStep('mapping');
      } catch (err) {
        toast.error("Error parsing CSV file");
        setFile(null);
      } finally {
        setIsParsing(false);
      }
    };
    reader.readAsText(file);
  };

  const handleCellEdit = (rowIndex: number, fieldKey: string, value: string) => {
    setCsvData(prev => {
      const newData = [...prev];
      const csvHeader = mapping[fieldKey];
      if (csvHeader) {
        newData[rowIndex] = { ...newData[rowIndex], [csvHeader]: value };
      }
      return newData;
    });
  };

  const handleImport = async () => {
    const missingRequired = EXPECTED_FIELDS.filter(f => f.required && !mapping[f.key]);
    if (missingRequired.length > 0) {
      toast.error(`Please map required fields: ${missingRequired.map(f => f.label).join(', ')}`);
      return;
    }

    setIsImporting(true);
    try {
      const finalLeads = csvData.map(row => {
        const lead: any = {};
        EXPECTED_FIELDS.forEach(field => {
          const csvHeader = mapping[field.key];
          lead[field.key] = csvHeader ? row[csvHeader] : (field.key === 'priority' ? 'Medium' : (field.key === 'source' ? 'Website' : ''));
        });
        return lead;
      }).filter(l => l.name && l.email);

      const res = await API.post('/leads/bulk-import', { 
        leads: finalLeads,
        skip_duplicates: skipDuplicates
      });
      toast.success(`Successfully imported ${res.data.count} leads${res.data.skipped ? ` (${res.data.skipped} skipped)` : ''}`);
      
      onOpenChange(false);
      setTimeout(() => {
        onImportSuccess();
      }, 1000);
    } catch (err) {
      toast.error("Failed to import leads");
    } finally {
      setIsImporting(false);
    }
  };

  const downloadSample = () => {
    const headers = "Student Name,Email,Phone Number,Applied Course,Lead Source,Priority,Notes";
    const body = "Aryan Khan,aryan@example.com,9876543210,B.Tech CSE,Website,High,Good profile";
    const blob = new Blob([headers + "\n" + body], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'sample_student_leads.csv';
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[900px] h-[92vh] max-h-[750px] flex flex-col p-0 overflow-hidden bg-background/95 dark:bg-slate-950/95 backdrop-blur-2xl border border-white/10 shadow-[0_50px_100px_rgba(0,0,0,0.4)] rounded-3xl transition-all duration-500">
        <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-emerald-500 via-teal-500 to-cyan-500" />
        
        <DialogHeader className="p-8 pb-4">
          <div className="flex items-center justify-between">
            <div className="space-y-1">
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-emerald-100/50 dark:bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 text-[10px] font-bold uppercase tracking-wider mb-2">
                <Settings size={12} />
                LMS Import Processor v3.0
              </div>
              <DialogTitle className="text-3xl font-black text-slate-900 dark:text-white tracking-tight leading-tight">
                Import <span className="text-transparent bg-clip-text bg-gradient-to-r from-emerald-600 to-teal-600 dark:from-emerald-400 dark:to-teal-400">Bulk Leads</span>
              </DialogTitle>
              <DialogDescription className="text-slate-500 dark:text-slate-400 max-w-[500px]">
                Scale your admissions pipeline with our high-end lead ingestion tool.
              </DialogDescription>
            </div>
            
            <div className="hidden md:flex items-center gap-3">
              {[
                { id: 'upload', icon: Upload },
                { id: 'mapping', icon: Settings },
                { id: 'review', icon: CheckCircle2 }
              ].map((s, idx) => (
                <React.Fragment key={s.id}>
                  <div className={cn(
                    "flex items-center justify-center h-10 w-10 rounded-2xl transition-all duration-300",
                    step === s.id ? "bg-primary text-primary-foreground shadow-lg scale-110" : "bg-white/5 text-slate-400"
                  )}>
                    <s.icon size={18} />
                  </div>
                  {idx < 2 && <ArrowRight size={14} className="text-slate-700" />}
                </React.Fragment>
              ))}
            </div>
          </div>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto px-8 py-4 custom-scrollbar">
          {step === 'upload' && (
            <div className="animate-in fade-in zoom-in duration-300">
              <div 
                onDragOver={(e) => { e.preventDefault(); e.stopPropagation(); }}
                onDrop={(e) => { e.preventDefault(); e.stopPropagation(); processFile(e.dataTransfer.files[0]); }}
                className={cn(
                  "relative h-[420px] border-2 border-dashed rounded-[2.5rem] flex flex-col items-center justify-center p-12 transition-all duration-500 group",
                  "border-white/10 hover:border-primary/50",
                  "bg-white/[0.02] hover:bg-white/[0.05]"
                )}
                onClick={() => fileInputRef.current?.click()}
              >
                <div className="absolute top-8 right-8 opacity-0 group-hover:opacity-100 transition-opacity duration-500 text-emerald-500/30 dark:text-emerald-500/10">
                  <MousePointer2 size={120} />
                </div>

                <div className="relative z-10 flex flex-col items-center text-center">
                  <div className="h-24 w-24 rounded-3xl bg-background border border-white/5 shadow-2xl flex items-center justify-center mb-8 group-hover:scale-110 transition-transform duration-500">
                    <Upload className="h-10 w-10 text-emerald-500 group-hover:animate-bounce" />
                  </div>
                  <h3 className="text-2xl font-bold text-slate-900 dark:text-white mb-3 tracking-tight">Drop Lead Records</h3>
                  <p className="text-slate-500 dark:text-slate-400 mb-10 max-w-sm mx-auto">
                    Drag and drop your spreadsheet file here, or click to browse files.
                  </p>
                  
                  <div className="flex items-center gap-4">
                    <Button variant="outline" className="rounded-2xl border-white/10 h-11 px-6 font-semibold hover:bg-white hover:text-black transition-colors">
                      Browse Files
                    </Button>
                    <Button variant="ghost" onClick={(e) => { e.stopPropagation(); downloadSample(); }} className="rounded-2xl h-11 px-6 text-slate-400 hover:text-emerald-500 font-semibold gap-2">
                      <Download size={16} />
                      Sample CSV
                    </Button>
                  </div>
                </div>
                
                <input 
                  type="file" 
                  ref={fileInputRef} 
                  className="hidden" 
                  accept=".csv" 
                  onChange={handleFileChange}
                />
              </div>

              <div className="mt-8 flex items-center justify-between px-2 text-slate-500">
                 <div className="flex gap-2">
                  {['Name*', 'Email*', 'Phone', 'Course'].map(f => (
                    <Badge key={f} variant="outline" className="rounded-lg text-[10px] font-medium border-white/5 bg-white/5 text-slate-400">
                      {f}
                    </Badge>
                  ))}
                </div>
                <div className="flex items-center gap-2 text-[10px] text-emerald-500 font-bold uppercase tracking-widest">
                  <Check size={14} />
                  Enterprise Data Security Enabled
                </div>
              </div>
            </div>
          )}

          {step === 'mapping' && (
            <div className="animate-in slide-in-from-bottom-4 duration-500 space-y-6">
              <div className="grid grid-cols-2 gap-8">
                <div className="space-y-4">
                  <div className="p-4 bg-emerald-500/5 rounded-2xl border border-emerald-500/10 flex gap-3">
                    <Info className="h-5 w-5 text-emerald-500 shrink-0" />
                    <div>
                      <h4 className="text-sm font-bold text-emerald-400">Map your columns</h4>
                      <p className="text-xs text-emerald-400/60 mt-0.5">Automated detection complete. Please verify the field mappings.</p>
                    </div>
                  </div>

                  <div className="space-y-2 max-h-[420px] pr-2 overflow-y-auto custom-scrollbar">
                    {EXPECTED_FIELDS.map(field => (
                      <div key={field.key} className="p-4 rounded-2xl bg-white/[0.02] border border-white/5 flex items-center justify-between group hover:border-emerald-500/50 transition-colors">
                        <div className="flex flex-col">
                          <label className="text-[10px] font-black text-slate-400 flex items-center gap-1.5 uppercase tracking-wider">
                            {field.label}
                            {field.required && <span className="text-red-500 text-lg leading-none mt-1">*</span>}
                          </label>
                          <span className="text-[10px] text-slate-500/60">System field</span>
                        </div>
                        
                        <div className="relative w-48">
                          <select 
                            value={mapping[field.key] || ''}
                            onChange={(e) => setMapping(prev => ({ ...prev, [field.key]: e.target.value }))}
                            className={cn(
                              "w-full h-10 px-3 pr-8 rounded-xl appearance-none bg-background border text-xs font-semibold transition-all focus:ring-2 focus:ring-emerald-500/20",
                              mapping[field.key] ? "border-emerald-500/50 text-emerald-400" : (field.required ? "border-red-500/30 text-slate-500" : "border-white/5 text-slate-500")
                            )}
                          >
                            <option value="">-- Ignore Field --</option>
                            {headers.map(h => (
                               <option key={h} value={h}>{h}</option>
                            ))}
                          </select>
                          <div className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none">
                            {mapping[field.key] ? <CheckCircle2 size={14} className="text-emerald-500" /> : <Settings size={14} className="text-slate-700" />}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="space-y-4">
                  <div className="bg-slate-950 p-6 rounded-[2rem] shadow-2xl relative overflow-hidden group min-h-[400px] border border-white/5">
                    <div className="absolute top-0 right-0 p-8 opacity-5 group-hover:opacity-10 transition-opacity">
                      <TableIcon size={200} className="text-white" />
                    </div>
                    
                    <div className="relative z-10 h-full flex flex-col">
                      <div className="flex items-center justify-between mb-6">
                        <h4 className="text-white font-bold flex items-center gap-2">
                          <MousePointer2 size={16} className="text-emerald-500" />
                          Live Preview
                        </h4>
                        <Badge variant="outline" className="bg-white/5 border-white/10 text-white/50 font-normal">
                          {csvData.length} Records
                        </Badge>
                      </div>

                      <div className="space-y-3">
                        {csvData.slice(0, 5).map((row, idx) => (
                          <div key={idx} className="p-4 rounded-xl bg-white/5 border border-white/5">
                            <div className="grid grid-cols-2 gap-4">
                              <div>
                                <p className="text-[10px] text-white/30 uppercase font-black tracking-widest">{mapping['name'] || 'Name'}</p>
                                <p className="text-sm font-bold text-white truncate">{row[mapping['name'] || headers[0]] || '—'}</p>
                              </div>
                              <div className="text-right">
                                <p className="text-[10px] text-white/30 uppercase font-black tracking-widest">{mapping['course'] || 'Course'}</p>
                                <p className="text-sm font-bold text-emerald-400 truncate">{row[mapping['course'] || headers[3]] || '—'}</p>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>

                      <div className="mt-auto pt-6 border-t border-white/5 flex items-center justify-center gap-2 text-[10px] text-white/20 font-bold uppercase tracking-widest">
                         LMS Data Ingestion Stream
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {step === 'review' && (
            <div className="animate-in slide-in-from-right-4 duration-500 space-y-4">
              <div className="flex items-center justify-between mb-2">
                <div className="flex gap-4">
                  <div className="flex items-center gap-2 bg-emerald-500/10 border border-emerald-500/20 px-3 py-1.5 rounded-xl text-emerald-500 text-[10px] font-bold uppercase tracking-wider">
                    <Check size={14} /> {csvData.length} records ready
                  </div>
                  <div className="flex items-center gap-2 bg-white/5 border border-white/5 px-3 py-1.5 rounded-xl text-slate-400 text-[10px] uppercase font-bold tracking-widest">
                    <MousePointer2 size={14} /> Click cell to fix typos
                  </div>
                </div>
                
                <div className="flex items-center gap-3 bg-white/5 px-4 py-2 rounded-2xl border border-white/5 shadow-sm">
                   <div className="flex items-center gap-2 cursor-pointer group" onClick={() => setSkipDuplicates(!skipDuplicates)}>
                      <div className={cn(
                        "w-8 h-4 rounded-full transition-colors relative",
                        skipDuplicates ? "bg-emerald-600" : "bg-slate-800"
                      )}>
                        <div className={cn(
                          "absolute top-0.5 w-3 h-3 bg-white rounded-full transition-all shadow-sm",
                          skipDuplicates ? "translate-x-4" : "translate-x-0"
                        )} />
                      </div>
                      <span className="text-[10px] font-black text-slate-300 uppercase tracking-tighter">Skip Duplicates</span>
                   </div>
                </div>
              </div>

              <div className="border border-white/5 rounded-[2rem] overflow-hidden bg-background/40 backdrop-blur-xl shadow-2xl">
                <div className="max-h-[450px] overflow-auto custom-scrollbar">
                  <Table>
                    <TableHeader className="bg-white/[0.02] sticky top-0 z-20 backdrop-blur-md">
                      <TableRow className="border-white/5 hover:bg-transparent">
                        {EXPECTED_FIELDS.filter(f => mapping[f.key]).map(field => (
                          <TableHead key={field.key} className="text-[10px] font-black text-slate-400 uppercase tracking-widest py-4 bg-transparent border-none">
                            {field.label}
                          </TableHead>
                        ))}
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {csvData.map((row, rowIndex) => (
                        <TableRow key={rowIndex} className="border-white/5 hover:bg-white/[0.02] group transition-colors">
                          {EXPECTED_FIELDS.filter(f => mapping[f.key]).map(field => {
                            const csvHeader = mapping[field.key];
                            const value = row[csvHeader] || '';
                            const isEditing = editingCell?.row === rowIndex && editingCell?.field === field.key;
                            const isMissing = field.required && !value;

                            return (
                              <TableCell 
                                key={field.key} 
                                className={cn(
                                  "py-3 text-[12px] font-medium transition-all group-hover:pl-5",
                                  isMissing ? "text-red-400" : "text-slate-300"
                                )}
                                onClick={() => setEditingCell({ row: rowIndex, field: field.key })}
                              >
                                {isEditing ? (
                                  <input 
                                    autoFocus
                                    className="bg-emerald-600/20 border border-emerald-500/50 rounded-lg px-2 py-1 w-full text-white outline-none focus:ring-2 ring-emerald-500/30"
                                    value={value}
                                    onChange={(e) => handleCellEdit(rowIndex, field.key, e.target.value)}
                                    onBlur={() => setEditingCell(null)}
                                    onKeyDown={(e) => e.key === 'Enter' && setEditingCell(null)}
                                  />
                                ) : (
                                  <div className="flex items-center justify-between">
                                    <span className="truncate max-w-[150px]">{value || 'Required'}</span>
                                    <Badge className="opacity-0 group-hover:opacity-100 transition-opacity bg-white/10 text-[8px] text-white/50 border-none px-1.5 py-0">
                                      Edit
                                    </Badge>
                                  </div>
                                )}
                              </TableCell>
                            );
                          })}
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              </div>
            </div>
          )}
        </div>

        <DialogFooter className="p-8 border-t border-white/10 bg-muted/10 backdrop-blur-md flex items-center justify-between gap-4">
          <div className="flex-1 flex items-center gap-2">
             {step === 'mapping' && (
               <div className="flex items-center gap-1.5 text-xs text-amber-500 font-semibold">
                 <AlertTriangle size={14} />
                 {EXPECTED_FIELDS.filter(f => f.required && !mapping[f.key]).length > 0 ? "Map all required fields" : "Ready to review!"}
               </div>
             )}
             {step === 'review' && (
               <div className="flex items-center gap-1.5 text-xs text-emerald-500 font-bold uppercase tracking-tighter">
                 <CheckCircle2 size={14} /> Data validated. Safe to ingest.
               </div>
             )}
          </div>

          <div className="flex gap-3">
            <Button variant="ghost" onClick={() => step === 'mapping' ? setStep('upload') : onOpenChange(false)} className="rounded-2xl h-12 px-8 font-bold text-slate-500">
              {step === 'upload' ? 'Cancel' : 'Back'}
            </Button>
            
            <Button 
              onClick={() => {
                if (step === 'upload') {
                  if (file) parseCSV(file);
                } else if (step === 'mapping') {
                  setStep('review');
                } else {
                  handleImport();
                }
              }} 
              disabled={step === 'upload' ? !file : (step === 'mapping' ? !mapping['name'] : isImporting)}
              className={cn(
                "rounded-2xl h-12 px-10 font-black shadow-xl transition-all duration-300 group",
                "bg-primary hover:opacity-90 text-primary-foreground min-w-[180px]"
              )}
            >
              {isImporting ? (
                <>
                  <div className="h-5 w-5 border-2 border-white/30 border-t-white rounded-full animate-spin mr-2" />
                  Ingesting...
                </>
              ) : (
                <>
                  {step === 'upload' ? 'Analyze File' : step === 'mapping' ? 'Review & Edit' : 'Final Ingest'}
                  <ChevronRight size={18} className="ml-2 group-hover:translate-x-1 transition-transform" />
                </>
              )}
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
